import React, { useState, useEffect } from 'react';
import Card, { CardHeader } from '../ui/Card';
import { memberProfileAPI, RoomOption } from '../../services/api';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Room preference (Phase 3d) — a private booking setting. When a booking is
 * placed, the assignment algorithm uses this room if it's free and fits the
 * group; otherwise it falls back to the best-fit available room.
 */
const RoomPreferenceCard: React.FC = () => {
  const [rooms, setRooms] = useState<RoomOption[]>([]);
  const [value, setValue] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [roomList, profile] = await Promise.all([
          memberProfileAPI.listRooms(),
          memberProfileAPI.getProfile(),
        ]);
        // Preference applies to studio assignment; pods are booked separately.
        setRooms(roomList.filter((r) => r.room_type === 'studio'));
        setValue(profile.preferred_room_id != null ? String(profile.preferred_room_id) : '');
      } catch {
        /* non-fatal */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    const prev = value;
    setValue(raw);
    setSaving(true);
    try {
      await memberProfileAPI.updateProfile({ preferred_room_id: raw === '' ? null : Number(raw) });
      toast.success('Room preference saved');
    } catch (err: any) {
      setValue(prev); // revert on failure
      toast.error(apiErrorMessage(err, 'Failed to save room preference'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <CardHeader title="Room Preference" />
      <p className="text-sm text-clinic-muted mb-3">
        We'll assign your preferred room when it's free and fits the group; otherwise the best-fit available room.
      </p>
      <select
        value={value}
        onChange={handleChange}
        disabled={loading || saving}
        className="w-full px-4 py-2.5 border border-clinic-primary-lighter rounded-button focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary text-clinic-dark bg-white transition-colors disabled:opacity-60"
      >
        <option value="">No preference (best-fit)</option>
        {rooms.map((r) => (
          <option key={r.id} value={String(r.id)}>
            {r.name} (fits {r.capacity})
          </option>
        ))}
      </select>
    </Card>
  );
};

export default RoomPreferenceCard;
